import { useEffect, useState } from "react";
import { CheckCircle2, FolderOpen, Image as ImageIcon, Info, Loader2, MessageSquare, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useZurueckSchliesst } from "@/lib/useZurueckSchliesst";
import MailSchreibenModal from "@/components/MailSchreibenModal";

/**
 * KundenEingaengeListe – Reiter „Vom Kunden“ der Mail-Zentrale (A-017 Schritt 1, 22.10.2026).
 * Je Einsende-Runde aus dem Kundenportal eine Karte: Vorschaubilder (Klick = groß), Kunde, Datum, Nachricht;
 * Projekt vorausgewählt (Portal-Link-Projekt → einziges offenes Projekt → Auswahlliste); „Ins Projekt übernehmen“
 * (Weg /kundenportal/admin/{id}/eingaenge/{idx}/uebertragen, doppelte Übernahme verhindert); danach Auswahlliste
 * „Nächster Schritt“; „Erledigt“ nimmt die Karte aus der Liste (Häkchen „erledigte zeigen“).
 * Props: kundeId (optional, nur dieser Kunde, z. B. Sprung aus dem Projekt), onKundeAufheben, onGeaendert (Zähler neu laden)
 */
const SCHRITTE = [
  ["danke_mail", "Danke-Mail an den Kunden"],
  ["angebot", "Angebot zu diesem Projekt erstellen"],
  ["termin", "Besichtigung / Termin anlegen"],
  ["einsatz", "Einsatz planen"],
  ["aufgabe", "Aufgabe anlegen"],
];
const SCHRITT_LABEL = Object.fromEntries(SCHRITTE);
const datum = (iso) => {
  try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};
const tag = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return ""; } };
const feld = "w-full h-10 rounded-sm border border-input bg-background px-3 text-sm";

const KundenEingaengeListe = ({ kundeId = "", onKundeAufheben, onGeaendert }) => {
  const [daten, setDaten] = useState(null);
  const [erledigte, setErledigte] = useState(false);
  const [gross, setGross] = useState(null);  // { src, name }
  useZurueckSchliesst(!!gross, () => setGross(null));   // BUT-0032: Zurück/Wischen schließt nur das große Bild
  const [mailFuer, setMailFuer] = useState(null);  // Karte für die Danke-Mail

  const laden = async () => {
    try {
      const r = await api.get("/kundenportal/eingaenge", { params: { erledigte, kunde_id: kundeId || undefined } });
      setDaten(r.data);
    } catch {
      setDaten({ eingaenge: [], offen: 0, fehler: true });
    }
  };
  useEffect(() => { laden(); }, [erledigte, kundeId]); // eslint-disable-line react-hooks/exhaustive-deps
  const neuLaden = () => { laden(); onGeaendert?.(); };

  const karten = daten?.eingaenge || [];
  const kundeName = kundeId && karten[0]?.kunde_id === kundeId ? karten[0].kunde_name : "";
  return (
    <div className="space-y-3" data-testid="eingaenge-liste">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Fotos und Nachrichten, die Kunden über das Kundenportal geschickt haben.</p>
        <label className="inline-flex items-center gap-2 text-sm py-1">
          <input type="checkbox" className="w-4 h-4" checked={erledigte} onChange={(e) => setErledigte(e.target.checked)} data-testid="eingaenge-erledigte" />
          erledigte zeigen
        </label>
      </div>
      {kundeId && (
        <div className="flex items-center gap-2 text-sm rounded-sm border bg-muted/40 px-3 py-2" data-testid="eingaenge-kundenfilter">
          Nur Kunde {kundeName ? <b>{kundeName}</b> : "aus dem Projekt"}
          <button type="button" onClick={onKundeAufheben} className="ml-auto text-primary hover:underline" data-testid="eingaenge-kundenfilter-weg">alle zeigen ✕</button>
        </div>
      )}
      {!daten && <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {daten?.fehler && <div className="text-sm text-red-700">Die Eingänge konnten nicht geladen werden.</div>}
      {daten && !daten.fehler && karten.length === 0 && (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="eingaenge-leer">
          {erledigte ? "Keine Eingänge vom Kunden." : "Keine offenen Eingänge vom Kunden. Alles erledigt."}
        </div>
      )}
      <div className="grid grid-cols-1 gap-3">
        {karten.map((k) => (
          <EingangKarte key={`${k.eintrag_id}-${k.idx}`} karte={k} onGross={setGross} onDankeMail={setMailFuer} onGeaendert={neuLaden} />
        ))}
      </div>

      {gross && (
        <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-3" onClick={(e) => { e.stopPropagation(); setGross(null); }} data-testid="eingang-bild-gross">
          <button type="button" className="absolute top-3 right-3 text-white p-2" aria-label="Schließen" onClick={(e) => { e.stopPropagation(); setGross(null); }}><X className="w-6 h-6" /></button>
          <img src={gross.src} alt={gross.name || "Foto"} className="max-w-full max-h-full object-contain" />
        </div>
      )}

      <MailSchreibenModal
        isOpen={!!mailFuer}
        onClose={() => setMailFuer(null)}
        kundeId={mailFuer?.kunde_id || ""}
        projekte={mailFuer?.projekte || []}
        projektId={mailFuer?.projektId || ""}
        vorlage={daten?.danke_vorlage || ""}
        titel="Danke-Mail an den Kunden"
        onGesendet={async () => {
          const k = mailFuer;
          setMailFuer(null);
          if (k) await api.patch(`/kundenportal/eingaenge/${k.eintrag_id}/${k.idx}`, { naechster_schritt: "danke_mail" }).catch(() => {});
          toast.success("Danke-Mail gesendet. Wenn nichts weiter zu tun ist: „Erledigt“.");
          neuLaden();
        }}
      />
    </div>
  );
};

const EingangKarte = ({ karte, onGross, onDankeMail, onGeaendert }) => {
  const navigate = useNavigate();
  const [bilder, setBilder] = useState(null);
  const [projekt, setProjekt] = useState(karte.uebernommen?.projekt_id || karte.vorschlag?.projekt_id || "");
  const [busy, setBusy] = useState(false);
  const [schritt, setSchritt] = useState("");
  const tid = `eingang-${karte.eintrag_id}-${karte.idx}`;

  useEffect(() => {
    if (!karte.fotos_anzahl) { setBilder([]); return undefined; }
    let lebt = true;
    api.get(`/kundenportal/eingaenge/${karte.eintrag_id}/${karte.idx}/fotos`)
      .then((r) => { if (lebt) setBilder(r.data?.fotos_data || []); })
      .catch(() => { if (lebt) setBilder([]); });
    return () => { lebt = false; };
  }, [karte.eintrag_id, karte.idx, karte.fotos_anzahl]);

  const projekte = karte.projekte || [];
  const offene = projekte.filter((p) => p.offen);
  const aktuell = projekte.find((p) => p.id === projekt);
  const titel = (p) => `${p.titel}${p.status ? ` · ${p.status}` : ""}`;
  const ueb = karte.uebernommen;
  const werkbank = (extra = "") => `/module/projekte/werkbank/${karte.kunde_id}${projekt ? `?projekt_id=${projekt}${extra}` : ""}`;

  const uebernehmen = async () => {
    if (!projekt) { toast.error("Bitte zuerst ein Projekt wählen."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/kundenportal/admin/${karte.eintrag_id}/eingaenge/${karte.idx}/uebertragen`, { projekt_id: projekt });
      const d = r.data || {};
      if (d.uebertragen === d.gesamt) toast.success(`${d.uebertragen} Foto(s) ins Projekt „${aktuell?.titel || ""}“ übernommen. Jetzt den nächsten Schritt wählen.`);
      else toast.warning(`${d.uebertragen} von ${d.gesamt} Fotos übernommen – die übrigen ließen sich nicht lesen.`);
      onGeaendert();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Übernehmen fehlgeschlagen");
      if (e?.response?.status === 409) onGeaendert();
    } finally { setBusy(false); }
  };

  const vermerken = (body) => api.patch(`/kundenportal/eingaenge/${karte.eintrag_id}/${karte.idx}`, body);

  const schrittAusfuehren = async () => {
    if (!schritt) return;
    if (schritt === "danke_mail") { onDankeMail({ ...karte, projektId: projekt }); return; }
    if (!projekt) { toast.error("Für diesen Schritt bitte zuerst ein Projekt wählen."); return; }
    await vermerken({ naechster_schritt: schritt }).catch(() => {});
    if (schritt === "angebot") navigate(`/quotes/new?customer=${karte.kunde_id}&projekt_id=${projekt}`);
    else if (schritt === "termin") navigate(werkbank("&tab=termine"));
    else if (schritt === "aufgabe") navigate(werkbank("&tab=aufgaben"));
    else if (schritt === "einsatz") navigate(`/einsaetze?projekt_id=${projekt}&kunde_id=${karte.kunde_id}&projekt_titel=${encodeURIComponent(aktuell?.titel || "")}`);
  };

  const erledigt = async (wert) => {
    if (wert && karte.fotos_anzahl && !ueb
      && !window.confirm("Die Fotos sind noch nicht im Projekt. Trotzdem als erledigt markieren?")) return;
    setBusy(true);
    try {
      await vermerken({ erledigt: wert });
      toast.success(wert ? "Erledigt – aus der Liste genommen." : "Wieder offen.");
      onGeaendert();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const grundText = {
    portal: "Vorausgewählt: das Projekt, für das der Portal-Link verschickt wurde.",
    einziges: "Vorausgewählt: das einzige offene Projekt dieses Kunden.",
  }[karte.vorschlag?.grund];
  const zeigeSchritt = !!ueb || !karte.fotos_anzahl;

  return (
    <div className={`rounded-md border bg-card p-3 sm:p-4 space-y-3 min-w-0 ${karte.erledigt ? "opacity-70" : ""}`} data-testid={tid}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <button type="button" onClick={() => navigate(`/module/projekte/werkbank/${karte.kunde_id}`)} className="text-base font-semibold hover:underline text-left break-words" data-testid={`${tid}-kunde`}>
            {karte.kunde_name}
          </button>
          <div className="text-xs text-muted-foreground">
            {datum(karte.datum)} · <ImageIcon className="inline w-3.5 h-3.5 -mt-0.5" /> {karte.fotos_anzahl} Foto(s)
          </div>
        </div>
        {karte.erledigt
          ? <span className="text-xs px-2 py-0.5 rounded-full bg-muted" data-testid={`${tid}-status`}>erledigt</span>
          : ueb
            ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid={`${tid}-status`}>übernommen</span>
            : <span className="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white" data-testid={`${tid}-status`}>neu</span>}
      </div>

      {karte.nachricht ? (
        <div className="flex gap-2 text-sm bg-muted/40 border-l-4 border-emerald-700 px-3 py-2 whitespace-pre-wrap break-words" data-testid={`${tid}-nachricht`}>
          <MessageSquare className="w-4 h-4 mt-0.5 shrink-0 text-emerald-700" /><span>{karte.nachricht}</span>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">Keine Nachricht, nur Fotos.</div>
      )}

      {karte.fotos_anzahl > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2" data-testid={`${tid}-bilder`}>
          {bilder === null && <div className="col-span-full py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin" /></div>}
          {(bilder || []).map((src, i) => (
            <button key={i} type="button" onClick={() => onGross({ src, name: karte.fotos[i] })} className="aspect-square rounded-sm overflow-hidden border bg-muted" title={karte.fotos[i] || `Foto ${i + 1}`} data-testid={`${tid}-bild-${i}`}>
              <img src={src} alt={karte.fotos[i] || `Foto ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {/* Projekt: Vorauswahl, sonst Auswahlliste (keine Knopfreihe) */}
      <div>
        <label className="flex items-center gap-1.5 text-sm font-medium mb-1" htmlFor={`${tid}-projekt`}>
          <FolderOpen className="w-4 h-4 text-emerald-700" /> Projekt
        </label>
        <select id={`${tid}-projekt`} value={projekt} onChange={(e) => setProjekt(e.target.value)} disabled={!!ueb || busy} className={feld} data-testid={`${tid}-projekt`}>
          <option value="">— bitte wählen —</option>
          {offene.map((p) => <option key={p.id} value={p.id}>{titel(p)}</option>)}
          {aktuell && !aktuell.offen && <option value={aktuell.id}>{titel(aktuell)} (abgeschlossen)</option>}
        </select>
        {!ueb && projekt && projekt === karte.vorschlag?.projekt_id && grundText && (
          <div className="mt-1 flex items-start gap-1.5 text-[11px] text-emerald-800 dark:text-emerald-300" data-testid={`${tid}-vorauswahl`}>
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{grundText}</span>
          </div>
        )}
        {!ueb && !projekt && offene.length > 1 && (
          <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-800 dark:text-amber-300" data-testid={`${tid}-bitte-waehlen`}>
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>Der Kunde hat {offene.length} offene Projekte. Bitte wählen Sie das passende.</span>
          </div>
        )}
        {!ueb && offene.length === 0 && !aktuell && (
          <div className="mt-1 flex flex-wrap items-start gap-1.5 text-[11px] text-amber-800 dark:text-amber-300" data-testid={`${tid}-kein-projekt`}>
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Der Kunde hat kein offenes Projekt. Bitte zuerst ein Projekt anlegen.</span>
            <button type="button" onClick={() => navigate(`/module/projekte/werkbank/${karte.kunde_id}`)} className="underline" data-testid={`${tid}-projekt-anlegen`}>Projekt anlegen</button>
          </div>
        )}
      </div>

      {ueb ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-emerald-800 dark:text-emerald-300" data-testid={`${tid}-uebernommen`}>
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>Am {tag(ueb.am)} {ueb.anzahl} Foto(s) ins Projekt „{ueb.projekt_titel}“ übernommen.</span>
          <button type="button" onClick={() => navigate(`/module/projekte/werkbank/${karte.kunde_id}?projekt_id=${ueb.projekt_id}&tab=bilder`)} className="underline" data-testid={`${tid}-bilder-ansehen`}>Bilder im Projekt ansehen</button>
        </div>
      ) : karte.fotos_anzahl > 0 && !karte.erledigt && (
        <button type="button" onClick={uebernehmen} disabled={busy || !projekt}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 rounded-sm bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50" data-testid={`${tid}-uebernehmen`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderOpen className="w-4 h-4" />} Ins Projekt übernehmen
        </button>
      )}

      {!karte.erledigt && zeigeSchritt && (
        <div className="rounded-sm border bg-muted/20 p-2 space-y-2" data-testid={`${tid}-schritt`}>
          <label className="block text-sm font-medium" htmlFor={`${tid}-schritt-select`}>Nächster Schritt</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <select id={`${tid}-schritt-select`} value={schritt} onChange={(e) => setSchritt(e.target.value)} className={`${feld} sm:flex-1`} data-testid={`${tid}-schritt-select`}>
              <option value="">— bitte wählen —</option>
              {SCHRITTE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <button type="button" onClick={schrittAusfuehren} disabled={!schritt || busy}
              className="h-10 px-4 rounded-sm border bg-background text-sm font-medium disabled:opacity-50 hover:bg-muted" data-testid={`${tid}-schritt-los`}>
              Weiter
            </button>
          </div>
          {karte.naechster_schritt && (
            <div className="text-[11px] text-muted-foreground" data-testid={`${tid}-schritt-zuletzt`}>Zuletzt gewählt: {SCHRITT_LABEL[karte.naechster_schritt]}</div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        {karte.erledigt ? (
          <button type="button" onClick={() => erledigt(false)} disabled={busy} className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-sm border text-sm hover:bg-muted" data-testid={`${tid}-wieder`}>
            <RotateCcw className="w-4 h-4" /> Wieder öffnen
          </button>
        ) : (
          <button type="button" onClick={() => erledigt(true)} disabled={busy} className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-sm border text-sm hover:bg-muted" data-testid={`${tid}-erledigt`}>
            <CheckCircle2 className="w-4 h-4" /> Erledigt
          </button>
        )}
      </div>
    </div>
  );
};

export default KundenEingaengeListe;
